import { IconButton, Input, Table, Text } from "@chakra-ui/react";
import { Control, Controller, useWatch } from "react-hook-form";
import type { CloseWorkOrderFormData } from "../type/WorkOrder";
import { formatPrice } from "@/utils/formatPrice";
import { LuTrash2 } from "react-icons/lu";
import { NumberInputField, NumberInputRoot } from "@/ui/chakra/number-input";

interface CloseWorkOrderVehicleTableProps {
  control: Control<CloseWorkOrderFormData>;
  fields: { id: string }[];
  remove: (index: number) => void;
}

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
  whiteSpace: "nowrap",
} as const;

const CloseWorkOrderVehicleTable: React.FC<CloseWorkOrderVehicleTableProps> = ({
  control,
  fields,
  remove,
}) => {
  const watchedItems = useWatch({ control, name: "vehicleItems" });

  return (
    <Table.ScrollArea borderWidth="1px" borderColor="border" rounded="lg">
      <Table.Root variant="line" size="sm">
        <Table.Header bg="bg.subtle">
          <Table.Row>
            <Table.ColumnHeader {...headerProps}>
              Descripción
            </Table.ColumnHeader>
            <Table.ColumnHeader {...headerProps}>Días</Table.ColumnHeader>
            <Table.ColumnHeader {...headerProps}>
              Valor unit. ($)
            </Table.ColumnHeader>
            <Table.ColumnHeader {...headerProps} textAlign="end">
              Valor total ($)
            </Table.ColumnHeader>
            <Table.ColumnHeader width="1%" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {fields.map((field, index) => {
            const item = watchedItems?.[index];
            const totalPrice =
              (Number(item?.days) || 0) * (Number(item?.unitPrice) || 0);

            return (
              <Table.Row key={field.id}>
                <Table.Cell minW="200px">
                  <Controller
                    name={`vehicleItems.${index}.description`}
                    control={control}
                    render={({ field: f }) => (
                      <Input
                        size="sm"
                        value={f.value}
                        onChange={f.onChange}
                        placeholder="Descripción del vehículo"
                      />
                    )}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Controller
                    name={`vehicleItems.${index}.days`}
                    control={control}
                    render={({ field: f }) => (
                      <NumberInputRoot
                        size="sm"
                        min={0}
                        w="110px"
                        value={String(f.value ?? 0)}
                        onValueChange={(e) =>
                          f.onChange(
                            isNaN(e.valueAsNumber) ? 0 : e.valueAsNumber,
                          )
                        }
                      >
                        <NumberInputField aria-label="Días" />
                      </NumberInputRoot>
                    )}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Controller
                    name={`vehicleItems.${index}.unitPrice`}
                    control={control}
                    render={({ field: f }) => (
                      <NumberInputRoot
                        size="sm"
                        min={0}
                        w="140px"
                        value={String(f.value ?? 0)}
                        onValueChange={(e) =>
                          f.onChange(
                            isNaN(e.valueAsNumber) ? 0 : e.valueAsNumber,
                          )
                        }
                      >
                        <NumberInputField aria-label="Valor unitario" />
                      </NumberInputRoot>
                    )}
                  />
                </Table.Cell>
                <Table.Cell textAlign="end">
                  <Text fontWeight="semibold">{formatPrice(totalPrice)}</Text>
                </Table.Cell>
                <Table.Cell>
                  <IconButton
                    aria-label="Eliminar fila"
                    size="sm"
                    colorPalette="red"
                    variant="ghost"
                    onClick={() => remove(index)}
                  >
                    <LuTrash2 />
                  </IconButton>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
};

export default CloseWorkOrderVehicleTable;
